/* eslint-disable @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage */
import { getTokenValue } from '@atlaskit/tokens';
import type tokens from '@atlaskit/tokens/dist/types/artifacts/token-names';
import { type ReadonlyVec2, vec2, vec3, vec4 } from 'gl-matrix';
import { defineMessage } from 'react-intl-next';

import { type TextAlignment } from '@canvas/adf';
import { Vector3 } from '@canvas/vector';

import { MAX_ELEMENTS } from './camera';
import {
  b100,
  b200,
  b300,
  b600,
  b800,
  g100,
  g150,
  g300,
  g600,
  g800,
  m100,
  m200,
  m300,
  m600,
  m800,
  n200,
  n300,
  n400,
  n600,
  n700,
  n800,
  o100,
  o300,
  o600,
  o800,
  p100,
  p200,
  p300,
  p600,
  p800,
  r100,
  r300,
  r600,
  r800,
  t100,
  t300,
  t600,
  t800,
  y100,
  y150,
  y250,
  y300,
  y600,
  y800,
  neutralLight,
  none,
  orange,
  red,
  teal,
  white,
  backgroundAccentBlue,
  backgroundAccentGreen,
  backgroundAccentMagenta,
  backgroundAccentOrange,
  backgroundAccentPurple,
  backgroundAccentRed,
  backgroundAccentTeal,
  backgroundAccentYellow,
  borderFocused,
  iconAccentBlue,
  iconAccentGray,
  iconAccentGreen,
  iconAccentMagenta,
  iconAccentOrange,
  iconAccentPurple,
  iconAccentRed,
  iconAccentTeal,
  iconAccentYellow,
  sectionTextGrey,
  sectionTextWhite,
  textAccentBlue,
  textAccentGreen,
  textAccentMagenta,
  textAccentOrange,
  textAccentPurple,
  textAccentRed,
  textAccentTeal,
  textAccentYellow,
  textDefaultColor,
  b700,
  r700,
  y700,
  p700,
  g700,
  t700,
  m700,
  o700,
  backgroundAccentBlueSubtlestHovered,
  backgroundAccentGreenSubtlestHovered,
  backgroundAccentMagentaSubtlestHovered,
  backgroundAccentOrangeSubtlestHovered,
  backgroundAccentPurpleSubtlestHovered,
  backgroundAccentRedSubtlestHovered,
  backgroundAccentTealSubtlestHovered,
  backgroundAccentYellowSubtlestHovered,
  background,
  backgroundDark,
} from './colors';
import { fallbackLineColors } from './fallback-colors';
import { DEFAULT_FONT_SCALE, MINIMUM_FONT_SCALE } from './font-scale';

export type Theme = 'light' | 'dark';

export const themeBackgroundColor = {
  light: background,
  dark: backgroundDark,
} satisfies Record<Theme, Vector3>;

// Current user selection color is always blue (#1D7AFC), as per functional spec
export const currentUserSelectionColor = Vector3.fromHexColor('#1D7AFC');
export const currentUserSelectionColorToken = 'color.border.focused';
// Multi selection internal color is always light blue (#85B8FF), as per functional spec
export const multiSelectionColor = Vector3.fromHexColor('#85B8FF');
export const multiSelectionColorToken = 'color.background.selected.pressed';
// Selection color of locked elements
export const lockedSelectionColor = Vector3.fromHexColor('#8993A5');
export const lockedSelectionColorToken = 'color.background.accent.gray.subtlest.pressed';

// Note: the order elements appear in this map matters for presentation
// Just keeping this here now for dark mode compatibility, which we don't officially support
export const oldStickyColorMap = {
  orange,
  b200,
  p200,
  m200,
  y150,
  g150,
  teal,
  red,
};

export const textColorMap = {
  textDefaultColor,
  textAccentBlue,
  textAccentRed,
  textAccentGreen,
  textAccentYellow,
  textAccentPurple,
  textAccentTeal,
  textAccentOrange,
  textAccentMagenta,
};

export const iconColorMap = {
  iconAccentGray,
  iconAccentBlue,
  iconAccentRed,
  iconAccentGreen,
  iconAccentYellow,
  iconAccentPurple,
  iconAccentTeal,
  iconAccentOrange,
  iconAccentMagenta,
};

export const backgroundColorMap = {
  neutralLight,
  backgroundAccentBlue,
  backgroundAccentRed,
  backgroundAccentGreen,
  backgroundAccentYellow,
  backgroundAccentPurple,
  backgroundAccentTeal,
  backgroundAccentOrange,
  backgroundAccentMagenta,
};

export const outlineColorMap = {
  n800,
  b800,
  r800,
  g800,
  y800,
  p800,
  t800,
  o800,
  m800,
};

export const strongestColorMap = {
  n600,
  b600,
  r600,
  g600,
  y600,
  p600,
  t600,
  o600,
  m600,
};

export const strongColorMap = {
  n400,
  b300,
  r300,
  g300,
  y300,
  p300,
  t300,
  o300,
  m300,
};

export const mediumColorMap = {
  n200,
  b200,
  red,
  g150,
  y150,
  p200,
  teal,
  orange,
  m200,
};

export const lightColorMap = {
  white,
  b100,
  r100,
  g100,
  y100,
  p100,
  t100,
  o100,
  m100,
};

export const miscellaneousColorMap = {
  borderFocused,
  sectionTextGrey,
  sectionTextWhite,
};

export const elementColorMap = {
  ...textColorMap,
  ...outlineColorMap,
  ...strongestColorMap,
  ...strongColorMap,
  ...mediumColorMap,
  ...lightColorMap,
  ...iconColorMap,
  ...backgroundColorMap,
  ...miscellaneousColorMap,
};

export const allTextColorMap = {
  ...textColorMap,
  ...iconColorMap,
  ...backgroundColorMap,
};

export const allPathColorMap = {
  ...strongestColorMap,
  ...strongColorMap,
  ...mediumColorMap,
};

export const allDefaultColorMap = {
  ...strongColorMap,
  ...mediumColorMap,
  ...lightColorMap,
};

export const allColorMap = {
  none,
  n300,
  y250, // Old yellow color, for backwards compatibility
  lockedSelectionColor,
  ...elementColorMap,
};

export const miscellaneousColorMapTokenNames: Record<string, string> = {
  borderFocused: 'color.border.focused',
  sectionTextGrey: 'color.text.accent.gray',
  sectionTextWhite: 'color.text.inverse',
};

export const textColorMapTokenNames: Record<string, TokenName> = {
  textDefaultColor: 'color.text',
  textAccentBlue: 'color.text.accent.blue',
  textAccentRed: 'color.text.accent.red',
  textAccentGreen: 'color.text.accent.green',
  textAccentYellow: 'color.text.accent.yellow',
  textAccentPurple: 'color.text.accent.purple',
  textAccentTeal: 'color.text.accent.teal',
  textAccentOrange: 'color.text.accent.orange',
  textAccentMagenta: 'color.text.accent.magenta',
};

export const iconColorMapTokenNames: Record<string, TokenName> = {
  iconAccentGray: 'color.icon.accent.gray',
  iconAccentBlue: 'color.icon.accent.blue',
  iconAccentRed: 'color.icon.accent.red',
  iconAccentGreen: 'color.icon.accent.green',
  iconAccentYellow: 'color.border.accent.yellow',
  iconAccentPurple: 'color.icon.accent.purple',
  iconAccentTeal: 'color.icon.accent.teal',
  iconAccentOrange: 'color.icon.accent.orange',
  iconAccentMagenta: 'color.icon.accent.magenta',
};

export const backgroundColorMapTokenNames: Record<string, TokenName> = {
  neutralLight: 'elevation.surface',
  backgroundAccentBlue: 'color.background.accent.blue.subtler',
  backgroundAccentRed: 'color.background.accent.red.subtler',
  backgroundAccentGreen: 'color.background.accent.green.subtlest.hovered',
  backgroundAccentYellow: 'color.background.accent.yellow.subtler',
  backgroundAccentPurple: 'color.background.accent.purple.subtler',
  backgroundAccentTeal: 'color.background.accent.teal.subtlest.hovered',
  backgroundAccentOrange: 'color.background.accent.orange.subtler',
  backgroundAccentMagenta: 'color.background.accent.magenta.subtler',
};

export const strongestColorMapTokenNames: Record<string, TokenName> = {
  n600: 'color.border.accent.gray',
  b600: 'color.border.accent.blue',
  r600: 'color.border.accent.red',
  g600: 'color.border.accent.green',
  y600: 'color.border.accent.yellow',
  p600: 'color.border.accent.purple',
  t600: 'color.border.accent.teal',
  o600: 'color.border.accent.orange',
  m600: 'color.border.accent.magenta',
};

export const strongColorMapTokenNames: Record<string, TokenName> = {
  n400: 'color.background.accent.gray.subtler.hovered',
  b300: 'color.background.accent.blue.subtlest.pressed',
  r300: 'color.background.accent.red.subtlest.pressed',
  g300: 'color.background.accent.green.subtlest.pressed',
  y300: 'color.background.accent.yellow.subtlest.pressed',
  p300: 'color.background.accent.purple.subtlest.pressed',
  t300: 'color.background.accent.teal.subtlest.pressed',
  o300: 'color.background.accent.orange.subtlest.pressed',
  m300: 'color.background.accent.magenta.subtlest.pressed',
};

export const mediumColorMapTokenNames: Record<string, TokenName> = {
  n200: 'color.background.accent.gray.subtlest',
  b200: 'color.background.accent.blue.subtler',
  red: 'color.background.accent.red.subtler',
  g150: 'color.background.accent.green.subtlest.hovered',
  y150: 'color.background.accent.yellow.subtler',
  p200: 'color.background.accent.purple.subtler',
  teal: 'color.background.accent.teal.subtlest.hovered',
  orange: 'color.background.accent.orange.subtler',
  m200: 'color.background.accent.magenta.subtler',
};

export const lightColorMapTokenNames: Record<string, TokenName> = {
  white: 'color.background.input',
  b100: 'color.background.accent.blue.subtlest',
  r100: 'color.background.accent.red.subtlest',
  g100: 'color.background.accent.green.subtlest',
  y100: 'color.background.accent.yellow.subtlest',
  p100: 'color.background.accent.purple.subtlest',
  t100: 'color.background.accent.teal.subtlest',
  o100: 'color.background.accent.orange.subtlest',
  m100: 'color.background.accent.magenta.subtlest',
};

export const outlineColorMapTokenNames: Record<string, TokenName> = {
  n800: 'color.text.accent.gray',
  b800: 'color.text.accent.blue',
  r800: 'color.text.accent.red',
  g800: 'color.text.accent.green',
  y800: 'color.text.accent.yellow',
  p800: 'color.text.accent.purple',
  t800: 'color.text.accent.teal',
  o800: 'color.text.accent.orange',
  m800: 'color.text.accent.magenta',
};

export type TokenName = keyof typeof tokens;

export const allColorMapTokenNames: Record<string, TokenName> = {
  none: 'color.text.accent.gray.bolder',
  y250: 'color.background.accent.yellow.subtler.hovered', // for backwards compatibility
  n300: 'color.background.accent.gray.subtlest.hovered',
  lockedSelectionColor: 'color.background.accent.gray.subtlest.pressed',
  ...lightColorMapTokenNames,
  ...mediumColorMapTokenNames,
  ...strongColorMapTokenNames,
  ...strongestColorMapTokenNames,
  ...backgroundColorMapTokenNames,
  ...iconColorMapTokenNames,
  ...textColorMapTokenNames,
  ...outlineColorMapTokenNames,
  ...miscellaneousColorMapTokenNames,
};

// Colors used in WDF https://hello.atlassian.net/wiki/spaces/CAN/pages/3797168860/WDF+Colours
export const darkPaletteDarkColorMapCanvasTokens: Record<string, string> = {
  'color.text': 'palette.dark.gray.300',
  'color.text.accent.blue': 'palette.dark.blue.300',
  'color.text.accent.red': 'palette.dark.red.300',
  'color.text.accent.green': 'palette.dark.green.300',
  'color.text.accent.yellow': 'palette.dark.yellow.300',
  'color.text.accent.purple': 'palette.dark.purple.300',
  'color.text.accent.teal': 'palette.dark.teal.300',
  'color.text.accent.orange': 'palette.dark.orange.300',
  'color.text.accent.magenta': 'palette.dark.magenta.300',
};

export const darkPaletteMediumColorMapCanvasTokens: Record<string, string> = {
  'color.icon.accent.gray': 'palette.dark.gray.200',
  'color.icon.accent.blue': 'palette.dark.blue.200',
  'color.icon.accent.red': 'palette.dark.red.200',
  'color.icon.accent.green': 'palette.dark.green.200',
  'color.border.accent.yellow': 'palette.dark.yellow.200',
  'color.icon.accent.purple': 'palette.dark.purple.200',
  'color.icon.accent.teal': 'palette.dark.teal.200',
  'color.icon.accent.orange': 'palette.dark.orange.200',
  'color.icon.accent.magenta': 'palette.dark.magenta.200',
};

export const darkPaletteLightColorMapCanvasTokens: Record<string, string> = {
  'elevation.surface': 'palette.dark.gray.100',
  'color.background.accent.blue.subtler': 'palette.dark.blue.100',
  'color.background.accent.red.subtler': 'palette.dark.red.100',
  'color.background.accent.green.subtlest.hovered': 'palette.dark.green.100',
  'color.background.accent.yellow.subtler': 'palette.dark.yellow.100',
  'color.background.accent.purple.subtler': 'palette.dark.purple.100',
  'color.background.accent.teal.subtlest.hovered': 'palette.dark.teal.100',
  'color.background.accent.orange.subtler': 'palette.dark.orange.100',
  'color.background.accent.magenta.subtler': 'palette.dark.magenta.100',
};

export const mediumPaletteDarkColorMapCanvasTokens: Record<string, string> = {
  'color.border.accent.gray': 'palette.medium.gray.300',
  'color.border.accent.blue': 'palette.medium.blue.300',
  'color.border.accent.red': 'palette.medium.red.300',
  'color.border.accent.green': 'palette.medium.green.300',
  'color.border.accent.yellow': 'palette.medium.yellow.300',
  'color.border.accent.purple': 'palette.medium.purple.300',
  'color.border.accent.teal': 'palette.medium.teal.300',
  'color.border.accent.orange': 'palette.medium.orange.300',
  'color.border.accent.magenta': 'palette.medium.magenta.300',
};

export const mediumPaletteMediumColorMapCanvasTokens: Record<string, string> = {
  'color.background.accent.gray.subtler.hovered': 'palette.medium.gray.200',
  'color.background.accent.blue.subtlest.pressed': 'palette.medium.blue.200',
  'color.background.accent.red.subtlest.pressed': 'palette.medium.red.200',
  'color.background.accent.green.subtlest.pressed': 'palette.medium.green.200',
  'color.background.accent.yellow.subtlest.pressed': 'palette.medium.yellow.200',
  'color.background.accent.purple.subtlest.pressed': 'palette.medium.purple.200',
  'color.background.accent.teal.subtlest.pressed': 'palette.medium.teal.200',
  'color.background.accent.orange.subtlest.pressed': 'palette.medium.orange.200',
  'color.background.accent.magenta.subtlest.pressed': 'palette.medium.magenta.200',
};

export const mediumPaletteLightColorMapCanvasTokens: Record<string, string> = {
  'color.background.accent.gray.subtlest': 'palette.medium.gray.100',
  'color.background.accent.blue.subtler': 'palette.medium.blue.100',
  'color.background.accent.red.subtler': 'palette.medium.red.100',
  'color.background.accent.green.subtlest.hovered': 'palette.medium.green.100',
  'color.background.accent.yellow.subtler': 'palette.medium.yellow.100',
  'color.background.accent.purple.subtler': 'palette.medium.purple.100',
  'color.background.accent.teal.subtlest.hovered': 'palette.medium.teal.100',
  'color.background.accent.orange.subtler': 'palette.medium.orange.100',
  'color.background.accent.magenta.subtler': 'palette.medium.magenta.100',
};

export const lightPaletteDarkColorMapCanvasTokens: Record<string, string> = {
  'color.background.accent.gray.subtler.hovered': 'palette.light.gray.300',
  'color.background.accent.blue.subtlest.pressed': 'palette.light.blue.300',
  'color.background.accent.red.subtlest.pressed': 'palette.light.red.300',
  'color.background.accent.green.subtlest.pressed': 'palette.light.green.300',
  'color.background.accent.yellow.subtlest.pressed': 'palette.light.yellow.300',
  'color.background.accent.purple.subtlest.pressed': 'palette.light.purple.300',
  'color.background.accent.teal.subtlest.pressed': 'palette.light.teal.300',
  'color.background.accent.orange.subtlest.pressed': 'palette.light.orange.300',
  'color.background.accent.magenta.subtlest.pressed': 'palette.light.magenta.300',
};

export const lightPaletteMediumColorMapCanvasTokens: Record<string, string> = {
  'color.background.accent.gray.subtlest': 'palette.light.gray.200',
  'color.background.accent.blue.subtler': 'palette.light.blue.200',
  'color.background.accent.red.subtler': 'palette.light.red.200',
  'color.background.accent.green.subtlest.hovered': 'palette.light.green.200',
  'color.background.accent.yellow.subtler': 'palette.light.yellow.200',
  'color.background.accent.purple.subtler': 'palette.light.purple.200',
  'color.background.accent.teal.subtlest.hovered': 'palette.light.teal.200',
  'color.background.accent.orange.subtler': 'palette.light.orange.200',
  'color.background.accent.magenta.subtler': 'palette.light.magenta.200',
};

export const lightPaletteLightColorMapCanvasTokens: Record<string, string> = {
  'color.background.input': 'palette.light.gray.100',
  'color.background.accent.blue.subtlest': 'palette.light.blue.100',
  'color.background.accent.red.subtlest': 'palette.light.red.100',
  'color.background.accent.green.subtlest': 'palette.light.green.100',
  'color.background.accent.yellow.subtlest': 'palette.light.yellow.100',
  'color.background.accent.purple.subtlest': 'palette.light.purple.100',
  'color.background.accent.teal.subtlest': 'palette.light.teal.100',
  'color.background.accent.orange.subtlest': 'palette.light.orange.100',
  'color.background.accent.magenta.subtlest': 'palette.light.magenta.100',
};

export type ColorName = keyof typeof allColorMap;
export type SkeletonColorName = keyof typeof fallbackLineColors;

export const ColorSwatchOptions = [
  'light',
  'medium',
  'strong',
  'strongest',
  'text',
  'icon',
  'background',
  'outline',
  'miscellaneous',
] as const;

export type ColorSwatch = (typeof ColorSwatchOptions)[number];

export const colorNames = Object.keys(allColorMap) as ColorName[];
export const skeletonColorNames = Object.keys(fallbackLineColors) as ColorName[];
export const elementColorNames = Object.keys(elementColorMap) as ColorName[];

export const ColorPaletteOptions = [
  'sticky',
  'shape',
  'path',
  'text',
  'section',
  'none',
] as const;
export type ColorPalette = (typeof ColorPaletteOptions)[number];

interface CollaboratorColor {
  telepointerColorToken: TokenName;
  telepointerFallbackColor: Vector3;
  stickyColor: Vector3;
  colorName: ColorName;
  // For the background of anonymous SVG avatars
  svgBackgroundColorToken: TokenName;
  svgBackgroundFallbackColor: Vector3;
}

/**
 * Telepointers have a higher intensity for contrast vs.
 * background color
 */
export const telepointerColors: Array<CollaboratorColor> = [
  {
    telepointerColorToken: 'color.background.accent.blue.bolder',
    telepointerFallbackColor: b700,
    stickyColor: b200,
    colorName: 'b200',
    svgBackgroundColorToken: 'color.background.accent.blue.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentBlueSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.red.bolder',
    telepointerFallbackColor: r700,
    stickyColor: red,
    colorName: 'red',
    svgBackgroundColorToken: 'color.background.accent.red.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentRedSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.purple.bolder',
    telepointerFallbackColor: p700,
    stickyColor: p200,
    colorName: 'p200',
    svgBackgroundColorToken: 'color.background.accent.purple.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentPurpleSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.green.bolder',
    telepointerFallbackColor: g700,
    stickyColor: g150,
    colorName: 'g150',
    svgBackgroundColorToken: 'color.background.accent.green.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentGreenSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.teal.bolder',
    telepointerFallbackColor: t700,
    stickyColor: teal,
    colorName: 'teal',
    svgBackgroundColorToken: 'color.background.accent.teal.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentTealSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.magenta.bolder',
    telepointerFallbackColor: m700,
    stickyColor: m200,
    colorName: 'm200',
    svgBackgroundColorToken: 'color.background.accent.magenta.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentMagentaSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.orange.bolder',
    telepointerFallbackColor: o700,
    stickyColor: orange,
    colorName: 'orange',
    svgBackgroundColorToken: 'color.background.accent.orange.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentOrangeSubtlestHovered,
  },
  {
    telepointerColorToken: 'color.background.accent.yellow.bolder',
    telepointerFallbackColor: y700,
    stickyColor: y150,
    colorName: 'y150',
    svgBackgroundColorToken: 'color.background.accent.yellow.subtlest.hovered',
    svgBackgroundFallbackColor: backgroundAccentYellowSubtlestHovered,
  },
];

export const textColor = {
  /**
   * HEX: #172b4d
   * RGB: 23, 43, 77
   * CLIPSPACE: RGB / 255
   */
  default: vec3.fromValues(0.09, 0.17, 0.3),
  /**
   * HEX: #42526E
   * RGB: 66, 82, 110
   * CLIPSPACE: RGB / 255
   */
  mention: vec3.fromValues(0.26, 0.32, 0.43),
};

export type LineColorName = keyof typeof lineColors;

export const lineColors = {
  black: vec4.fromValues(0, 0, 0, 255),
  n700: vec4.fromValues(n700.x, n700.y, n700.z, 255),
};

export enum TextLevel {
  Mixed = -1,
  Normal = 0,
  Heading1 = 1,
  Heading2 = 2,
  Heading3 = 3,
  Heading4 = 4,
  Heading5 = 5,
  Heading6 = 6,
}

export type ToolbarItemId =
  | 'color'
  | 'textFormat'
  | 'textLevel'
  | 'bullets'
  | 'textAlignment'
  | 'linkSwitcher'
  | 'lock'
  | 'unlock'
  | 'fullscreen'
  | 'createMenu'
  | 'openLink'
  | 'download'
  | 'connectorType'
  | 'pathCapStart'
  | 'pathCapEnd'
  | 'pathStyle'
  | 'generateLink'
  | 'linkIssues'
  | 'shape'
  | 'configureAction'
  | 'updateConfiguredAction'
  | 'shadow'
  | 'addLabel'
  | 'genericModuleItem';

export const TEXT_MIN_WIDTH = 18; // minimum width to contain a wide character of paragraph-sized text. This MUST NOT be larger than textBoardSize[0]
export const DEFAULT_TEXT_BOX_PADDING = 8;

export const stickyPadding = {
  horizontal: vec2.fromValues(12, 12),
  vertical: vec2.fromValues(12, 12),
};

export const STICKY_AUTHOR_LABEL_HEIGHT = 12;
export const STICKY_AUTHOR_TOP_PADDING = 6;
export const STICKY_AUTHOR_BOUNDING_HEIGHT =
  STICKY_AUTHOR_LABEL_HEIGHT + STICKY_AUTHOR_TOP_PADDING;
export const stickyPaddingWithAuthor = {
  horizontal: stickyPadding.horizontal,
  vertical: vec2.fromValues(
    stickyPadding.vertical[0],
    stickyPadding.vertical[1] + STICKY_AUTHOR_BOUNDING_HEIGHT
  ),
};

export const stickyBoardSize = vec2.fromValues(144, 144);

export const sectionBoardSize = vec2.fromValues(640, 320);
export const minTextBoardContentSize: ReadonlyVec2 = vec2.fromValues(TEXT_MIN_WIDTH, 22);
export const minTextBoardSize: ReadonlyVec2 = vec2.fromValues(
  minTextBoardContentSize[0] + DEFAULT_TEXT_BOX_PADDING * 2,
  minTextBoardContentSize[1] + DEFAULT_TEXT_BOX_PADDING * 2
);
export const textBoardSize = vec2.fromValues(
  // The final/rendered text width gets measured to 68 because of 'Add text' showing up in the editor. This isn't accounted for in the board's size
  76,
  minTextBoardSize[1]
);
export const lineBoardSize = vec2.fromValues(144, 144);
export const pathBoardSize = 144;
export const dynamicPathBoardSize = vec2.fromValues(144, 181); // 181 is calculated from the width/height ratio of the dynmaic icon path

/**
 * Keeps the minimum sizes consistent.
 *
 * Use this value when you need to specify a minimum size for an entity but the specific values of the dimensions isn't described by
 * design or that design just requests that it should be consistent with the
 * rest of the app.
 *
 * As of 2022-10-05 value applies to images, shapes, stickies and stamps.
 */
export const defaultMinSize = vec2.fromValues(24, 24);

export const MAX_ELEMENT_ZINDEX = MAX_ELEMENTS - 100; // arbitrary buffer for UI we want to render above elements in webgl
export const SELECTION_ZINDEX = MAX_ELEMENT_ZINDEX + 1;
export const ELEMENT_TOOLBAR_ZINDEX = SELECTION_ZINDEX + 1;
export const CONTEXT_MENU_ZINDEX = ELEMENT_TOOLBAR_ZINDEX + 1;
export const CUSTOM_CURSOR_ZINDEX = CONTEXT_MENU_ZINDEX + 1;

export const STICKY_MIN_HEIGHT = stickyBoardSize[1];
export const DEFAULT_STICKY_WIDTH = stickyBoardSize[0];
export const WIDE_STICKY_WIDTH = 240;
export const STICKY_RESIZE_BREAKPOINT = (WIDE_STICKY_WIDTH - stickyBoardSize[0]) / 2;
export const DEFAULT_BORDER_THICKNESS = 2;
export const LOCKED_BORDER_THICKNESS = 1;
export const ANCHOR_HOVER_RADIUS = 10;
export const DEFAULT_OUTLINE_WIDTH = 3;
export const DEFAULT_SHAPE_WIDTH = 144;
export const MINIMUM_SECTION_SIZE = vec2.fromValues(160, 160);
export const DEFAULT_SECTION_TITLE_PADDING = 8;
export const DEFAULT_LINE_LABEL_PADDING = 8;

export const TEMPLATE_VR_URL_PARAM_KEY = 'templateKey';

export { DEFAULT_FONT_SCALE, MINIMUM_FONT_SCALE };

/**
 * The pixel range of each distance field in the generated font texture atlas.
 */
export const SDF_PIXEL_RANGE = 5;

export { getColorName } from './get-color-name';
export {
  getColorVec3,
  getColorVec3FromToken,
  getColorCSSValue,
  getCurrentUserSelectionColor,
  getRoundedRectColorVec4,
  getAlignmentGuideColor,
  getThemeFromThemeState,
  getColorVec4,
  getCollaboratorColor,
  getCollaboratorColors,
  getCollaboratorSVGBackgroundColor,
  getCollaboratorStickyColor,
} from './get-color-value';
export { getColorMapFromElement } from './get-color-map-from-element';

export const defaultStickyColor = y150;
export const defaultShapeColor = p300;
export const defaultSectionColor = teal;
export const defaultPathLabelColor = textDefaultColor;
export const DEFAULT_SECTION_TITLE_WIDTH = 140;

// Shadow colors that appear underneath a smart section when dragging an issue in
export const sectionShadowDropzone = vec4.fromValues(56, 139, 255, 90);
export const sectionShadowDropzoneToken = 'color.border.focused';

const getSectionShadowDropzoneColor = (): vec4 => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const tokenValue = getTokenValue(sectionShadowDropzoneToken);
  return tokenValue
    ? vec4.fromValues(
        ...(Vector3.fromHexColor(tokenValue).toArray() as [number, number, number]),
        90
      )
    : sectionShadowDropzone;
};

export const shadowLightTheme = {
  color: vec4.fromValues(23, 43, 77, 48),
  shadowOffset: vec2.fromValues(0, 3),
  shadowPadding: vec2.fromValues(1, 0),
  shadowBlur: 3,
};
export const shadowDarkTheme = {
  color: vec4.fromValues(3, 4, 4, 51),
  shadowOffset: vec2.fromValues(0, 3),
  shadowPadding: vec2.fromValues(1, 0),
  shadowBlur: 3,
};
export const shadowSmartLinkLightTheme = {
  color: vec4.fromValues(8, 32, 67, 125),
  shadowOffset: vec2.fromValues(0, 1),
  shadowPadding: vec2.fromValues(-2.5, -2.5),
  shadowBlur: 2,
};
export const shadowSmartLinkDarkTheme = {
  color: vec4.fromValues(4, 4, 4, 191),
  shadowOffset: vec2.fromValues(0, 1),
  shadowPadding: vec2.fromValues(-2.5, -2.5),
  shadowBlur: 2,
};
export const shadowDropzoneLightTheme = () => ({
  color: getSectionShadowDropzoneColor(),
  shadowOffset: vec2.fromValues(0, 2),
  shadowPadding: vec2.fromValues(4, 4),
  shadowBlur: 3,
});

export const shadowDropzoneDarkTheme = () => ({
  color: getSectionShadowDropzoneColor(),
  shadowOffset: vec2.fromValues(0, 2),
  shadowPadding: vec2.fromValues(5, 3),
  shadowBlur: 3,
});

export const defaultStickyAlignment = 'center' as TextAlignment;
export const defaultShapeAlignment = 'center' as TextAlignment;
export const defaultTextAlignment = 'left' as TextAlignment;

/**
 * The units of space we delegate for the spacing between elements that we
 * desire in order to call it 'tidied'. Currently decided by simple eye test.
 */
export const TIDY_UP_SPACING = 10;

/**
 * The default line label height and width when it is created and it has its
 * 'Add Text' placeholder
 */
export const DEFAULT_PATH_LABEL_HEIGHT = 38;
export const DEFAULT_PATH_LABEL_WIDTH = 78;
/**
 * Represents the default value that the section title will initialize to when
 * not specified at the creation of a section
 *
 * Note that this is not the same as {@link ELEMENT_LABEL_PLACEHOLDER_TEXT}
 */
export const DEFAULT_SECTION_TITLE = '';

/**
 * If whether to scale padding with the font is not specified for a given
 * entity, this is the value that it will default to.
 */
export const DEFAULT_FONT_SCALED_PADDING = true;
/**
 * If a padding value isn't specified for a given entity, this is the value
 * that will default to.
 */
export const DEFAULT_PADDING = 0;

/**
 * Represents the value that will be displayed, in place of the real section
 * title, if the real title is empty
 *
 * Note that this is not the same as {@link DEFAULT_SECTION_TITLE}
 */

export const ELEMENT_LABEL_PLACEHOLDER_TEXT = defineMessage({
  id: 'whiteboards.constants.element-label-placeholder',
  defaultMessage: 'Give this a name',
  description: 'The placeholder for an element label',
});

export const TEXT_BOX_PLACEHOLDER_TEXT = defineMessage({
  id: 'whiteboards.constants.text-box-placeholder',
  defaultMessage: 'Add text',
  description: 'The placeholder for a text box',
});

export const TIMER_ID = 'board-timer';
export const TIMER_MAX_SECONDS = 59;
export const TIMER_MAX_MINUTES = 99;
export const TIMER_MAX_TIME = TIMER_MAX_MINUTES * 60000 + TIMER_MAX_SECONDS * 1000;
export const TIMER_DEFAULT_TIME = 5 * 60000;
export { TEMPLATE_KEY } from './get-template';

export const TEXT_BOX_PLACEHOLDER_PROPERTIES = {
  alignment: 'left' as const,
  width: null,
  fontScale: DEFAULT_FONT_SCALE,
  padding: DEFAULT_TEXT_BOX_PADDING,
};

export const SECTION_TITLE_PLACEHOLDER_PROPERTIES = {
  alignment: 'left' as const,
  width: null,
  fontScale: DEFAULT_FONT_SCALE,
  padding: DEFAULT_SECTION_TITLE_PADDING,
};

export const SMART_SECTION_SNACKBAR_TIMEOUT = 3000;
export const SMART_SECTION_UPDATE_LIMIT = 20;

/**
 * Supported file types for adding images.
 */
const supportedImageMimeTypesConst = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
] as const;

/**
 * Supported file types for importing files.
 */
const supportedFileImportMimeTypesConst = ['application/pdf'] as const;

export type FileType =
  | (typeof supportedImageMimeTypesConst)[number]
  | (typeof supportedFileImportMimeTypesConst)[number];

export const supportedImageMimeTypes = supportedImageMimeTypesConst as readonly string[];
export const supportedFileImportMimeTypes =
  supportedFileImportMimeTypesConst as readonly string[];

export type Site = {
  hostname: string;
  cloudId: string;
  standalone?: boolean;
};

/**
 * Maps a Site object to a URL string.
 *
 * @example
 *
 * ```
 * {
 *   hostname: 'hello.atlassian.net',
 *   cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
 * }
 * ```
 *
 * becomes `https://hello.atlassian.net`
 */
export const urlStringFromSite = (site: Site) => `https://${site.hostname}`;

export const productFabricSite = {
  hostname: 'product-fabric.atlassian.net',
  cloudId: 'DUMMY-158c8204-ff3b-47c2-adbb-a0906ccc722b',
} satisfies Site;

export const helloSite = {
  hostname: 'hello.atlassian.net',
  cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
} satisfies Site;

export const pugSite = {
  hostname: 'pug.jira-dev.com',
  cloudId: 'DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5',
} satisfies Site;

export const hamsFreeSitesStaging = [
  {
    hostname: 'atl-fabric-free-stg-hams-post-2.jira-dev.com',
    cloudId: 'e4716818-0f2f-423a-9890-8e75d16665f5',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-3.jira-dev.com',
    cloudId: '5c1e87de-6dab-466e-af3b-7c2beb3f2a2d',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-4.jira-dev.com',
    cloudId: 'ecdb70ba-c4e7-43e9-adcc-76c04e3a3c23',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-5.jira-dev.com',
    cloudId: '80351849-5493-45de-adcb-5f90ba13e7c7',
  },
] satisfies Site[];

export const ccpFreeSitesStaging = [
  {
    hostname: 'atl-fabric-free-stg-ccp-post-3.jira-dev.com',
    cloudId: 'b8a161ad-9ede-472d-ad36-9edb1cc5c819',
  },
  {
    hostname: 'atl-fabric-free-stg-ccp-post-4.jira-dev.com',
    cloudId: 'df7f7fc9-da5e-49a1-8175-e3566aa09190',
  },
  {
    hostname: 'atl-fabric-free-stg-ccp-post-5.jira-dev.com',
    cloudId: '1a5a5ff1-60fa-4205-b603-3e5743501acc',
  },
  {
    hostname: 'atl-fabric-free-stg-ccp-post-6.jira-dev.com',
    cloudId: '3eaeda37-90a4-4282-961c-7aea764a6866',
  },
] satisfies Site[];

export const hamsStandardSitesStaging = [
  {
    hostname: 'atl-fabric-standard-stg-hams-post.jira-dev.com',
    cloudId: '30eb9944-8420-4a4e-bd3c-ee97d342f19a',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-2.jira-dev.com',
    cloudId: 'f5c36c68-293b-4197-bd90-9bb21cdc8939',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-5.jira-dev.com',
    cloudId: 'e45ef192-f7cf-4b0c-b579-b08d4b1c4e41',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-6.jira-dev.com',
    cloudId: 'ccb836b1-a467-47c3-9663-78a4096be3df',
  },
] satisfies Site[];

export const hamsFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-hams-post-2.atlassian.net',
    cloudId: '03d6d989-78de-4f6e-8e06-1287a4f6dea0',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-3.atlassian.net',
    cloudId: '8b003e9c-9cb9-413f-a9ce-b47bf4df1952',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-4.atlassian.net',
    cloudId: '796eceed-86e2-44b5-b55a-723dc9ffe7a2',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-5.atlassian.net',
    cloudId: 'ce96d1d5-e8ac-4c3d-a95b-35f4e163c296',
  },
] satisfies Site[];

export const ccpFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-ccp-post.atlassian.net',
    cloudId: '679df76d-60ce-4655-851a-595eceba1fa7',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-2.atlassian.net',
    cloudId: '89fbe81f-9856-4456-b6f4-3c7b2869926b',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-3.atlassian.net',
    cloudId: '04a5c370-a66a-42fa-9782-b7782cb7a184',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-4.atlassian.net',
    cloudId: 'f9019ec2-9b70-4cb2-bce3-8e9a711bf1b6',
  },
] satisfies Site[];

export const hamsStandardSitesProduction = [
  {
    hostname: 'atl-fabric-standard-prod-hams-post.atlassian.net',
    cloudId: '8367ed66-c82a-45ef-9fa1-b1f7318ba50b',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-2.atlassian.net',
    cloudId: '628b5002-e83b-41d0-a5a5-633462342aaf',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-3.atlassian.net',
    cloudId: 'bf9bd1d0-c02a-41b1-8129-773f2b4d12c5',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-4.atlassian.net',
    cloudId: 'ffb36fd1-89b4-4f91-88ac-8411b6997d78',
  },
] satisfies Site[];

export const internalSites: ReadonlyArray<Site> = [
  helloSite,
  pugSite,
  productFabricSite,
  ...hamsFreeSitesStaging,
  ...ccpFreeSitesStaging,
  ...hamsStandardSitesStaging,
  ...hamsFreeSitesProduction,
  ...ccpFreeSitesProduction,
  ...hamsStandardSitesProduction,
  // Access has been removed
  {
    hostname: 'canvas-development.atlassian.net',
    cloudId: '09b342d9-ac7a-4709-9d9f-80d1d99f36f9',
    standalone: true,
  },
  {
    hostname: 'fabric-in-confluence.atlassian.net',
    cloudId: 'eb0e8137-863c-410e-b012-5a19ad5196a4',
  },
  {
    hostname: 'fabric-in-confluence.jira-dev.com',
    cloudId: 'b5b906eb-2bb0-4c53-b9da-b21685f952ab',
  },
  {
    hostname: 'fabric-dare.jira-dev.com',
    cloudId: 'b817aaf3-da8a-49a5-aa26-609e4ff9621b',
  },
  {
    hostname: 'fabric-restrictions.jira-dev.com',
    cloudId: 'f7fcdc63-70d1-4f9a-8987-00242c92f260',
    // Failing at the moment so marked as standalone to avoid HTTP errors
    standalone: true,
  },
  {
    hostname: 'canvas.dev.atlassian.com',
    cloudId: '382e7407-d3b8-4b14-970b-d209d87ee26c',
    standalone: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post.atlassian.net',
    cloudId: '589a65b6-1bf2-4441-ab30-73ebe26da1be',
  },
  {
    hostname: 'atl-fabric-premium-prod-hams-post.atlassian.net',
    cloudId: '70738083-9588-4515-9561-ee0dc7436a64',
  },
  {
    hostname: 'atl-fabric-standard-prod-ccp-post.atlassian.net',
    cloudId: 'ac78637d-e34d-4224-b592-5b6adc1dcb88',
  },
  {
    hostname: 'atl-fabric-premium-prod-ccp-post.atlassian.net',
    cloudId: 'a9faf70f-cf39-4e29-9be0-1a4a9a550218',
  },
  {
    hostname: 'atl-fabric-free-prod-hams.atlassian.net',
    cloudId: '899464ae-5fc9-4509-8e39-405d7fd834e4',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams.atlassian.net',
    cloudId: 'b58f599c-57fc-4c64-ac3d-8a00e32b4a5e',
  },
  {
    hostname: 'atl-fabric-premium-prod-hams.atlassian.net',
    cloudId: '07091ff6-51b1-4e2b-b4b7-c62b07655885',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp.atlassian.net',
    cloudId: 'a26afeb7-bac3-47e0-a51d-97f42a2644bc',
  },
  {
    hostname: 'atl-fabric-standard-prod-ccp.atlassian.net',
    cloudId: 'fd3f1e69-4df6-411e-a390-f1acb2cc6b0e',
  },
  {
    hostname: 'atl-fabric-premium-prod-ccp.atlassian.net',
    cloudId: '2855216c-8de3-494c-add5-7ad28e25642e',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post.jira-dev.com',
    cloudId: '949012ad-53dd-4fc4-8312-fb95c5015651',
  },
  {
    hostname: 'atl-fabric-premium-stg-hams-post.jira-dev.com',
    cloudId: '5ba39301-2ee9-4e60-887b-e363f9b4ae06',
  },
  {
    hostname: 'atl-fabric-standard-stg-ccp-post2.jira-dev.com',
    cloudId: 'c16160c0-c278-4ea8-b8ac-8db99c7d32a6',
  },
  {
    hostname: 'atl-fabric-premium-stg-ccp-post2.jira-dev.com',
    cloudId: 'd7db70c8-9d75-426e-af36-39e7f6194634',
  },
  {
    hostname: 'atl-fabric-free-stg-hams.jira-dev.com',
    cloudId: '835d0e64-6a7d-46f3-939f-08159597d05d',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams.jira-dev.com',
    cloudId: 'ce1605d4-f52c-4c4d-9c86-7421b6450137',
  },
  {
    hostname: 'atl-fabric-premium-stg-hams.jira-dev.com',
    cloudId: '78fa71b0-1f1d-44f5-aad7-d5ab438eb53e',
  },
  {
    hostname: 'atl-fabric-free-stg-ccp.jira-dev.com',
    cloudId: 'c0d82963-8b2c-436f-a9d3-91700185aad8',
  },
  {
    hostname: 'atl-fabric-standard-stg-ccp.jira-dev.com',
    cloudId: 'c3dca143-aa42-47b8-83e7-ddf1d70ef347',
  },
  {
    hostname: 'atl-fabric-premium-stg-ccp.jira-dev.com',
    cloudId: 'd1f0859b-41af-46e6-9184-b1290c8b4a32',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-non-admin.jira-dev.com',
    cloudId: '564847d0-3769-45d1-a760-d933197f4c4b',
  },
];

export const BOARD_ELEMENT_LIMIT = 5000;

/**
 * Use this if you need to a static placeholder value
 */
export const EXAMPLE_LOCALE = 'en-US';

/**
 * @internal Use {@link getDefaultLocale} or {@link EXAMPLE_LOCALE} instead unless you have a good reason to specify American English explicitly as the locale
 */
export const AMERICAN_ENGLISH = 'en-US';
/**
 * Note: Although this is not a constant, it's been added here as a incremental migration off of an old constant called "DEFAULT_LOCALE".
 * This was added to support anonymous mode browser locales
 */
export const getDefaultLocale = () => {
  return globalThis.navigator?.languages?.[0] ?? AMERICAN_ENGLISH;
};

/**
 * Offset to account for the height of the bottom toolbar, use this with
 * function toWorldScale() to get the right height regardless of zoom
 */
export const BOTTOM_TOOLBAR_OFFSET = vec2.fromValues(0, 158);

export const CANVAS_ELEMENT_ID = 'canvas-main';
export const UNDERLAY_ELEMENT_ID = 'canvas-underlay';
export const BASE_OVERLAY_ELEMENT_ID = 'canvas-overlay';
export const TELEPOINTER_OVERLAY_ELEMENT_ID = 'canvas-telepointer-overlay';
export const COMMENT_OVERLAY_ELEMENT_ID = 'canvas-comment-overlay';
export const TOPMOST_OVERLAY_ELEMENT_ID = 'canvas-topmost-overlay';

export const OVERLAY_ELEMENT_IDS = [
  BASE_OVERLAY_ELEMENT_ID,
  TELEPOINTER_OVERLAY_ELEMENT_ID,
  COMMENT_OVERLAY_ELEMENT_ID,
  TOPMOST_OVERLAY_ELEMENT_ID,
] as const;

export type DOMOverlayElementId = (typeof OVERLAY_ELEMENT_IDS)[number];

export type RenderingDOMElementId =
  | typeof CANVAS_ELEMENT_ID
  | typeof UNDERLAY_ELEMENT_ID
  | DOMOverlayElementId;

export const TOOLBAR_ID = 'toolbar-menu';

// toolbar has set height 82px (74px height plus 8px of the spotlight padding)
// (components/toolbar/src/toolbar.module.css)
export const TOOLBAR_HEIGHT = 82;
